import { defineStyleConfig } from "@chakra-ui/react";

import { focusIndicator } from "theme/tokens/accessibility";

/**
 * Decorative artwork on the field's right edge. Components opt in with
 * `data-flair` and supply the image through `--input-flair` (see
 * `components/input/inputFlair.ts`). Purely additive: the field keeps its own
 * variant colors, so pair it with a dark code field.
 */
const flairStyles = {
  "&[data-flair]": {
    backgroundImage: "var(--input-flair)",
    backgroundRepeat: "no-repeat",
    backgroundPosition: "right center",
    backgroundSize: "auto 100%",
  },
};

export const Input = defineStyleConfig({
  defaultProps: {
    size: "md",
    variant: "outline",
  },
  baseStyle: {
    borderRadius: "lg",
    field: flairStyles,
  },
  variants: {
    outline: {
      element: {
        fill: "fg.muted",
      },
      field: {
        textStyle: "base",
        _placeholder: {
          opacity: 1,
          color: "fg.subtle",
        },
        borderRadius: "lg",
        height: "9",
        px: "2",
        borderColor: "neutral.emphasized",
        _hover: {
          borderColor: "highlight.solid",
          "& + div": {
            //to control color of search icon
            fill: "fg.emphasized",
          },
        },
        _focus: {
          ...focusIndicator,
          "& + div": {
            fill: "fg.emphasized",
          },
        },
        _invalid: {
          borderColor: "error.solid",
          borderWidth: "1px",
          boxShadow: "none",
        },
        _disabled: {
          borderColor: "neutral.emphasized",
          color: "fg.subtle",
          cursor: "default",
          opacity: 1,
          _hover: {
            borderColor: "neutral.solid",
          },
          _placeholder: {
            color: "fg.subtle",
          },
          "& + div": {
            fill: "fg.subtle",
          },
        },
      },
    },
    edit: {
      field: {
        textStyle: "base",
        padding: 0,
        borderRadius: 0,
        height: "5",
        border: "none",
        borderBottom: "1px solid",
        borderColor: "neutral.muted",
        _invalid: {
          borderColor: "error.solid",
        },
      },
    },
    //variant for Search component
    line: {
      element: {
        width: "4",
        fill: "fg.muted",
      },
      field: {
        _placeholder: {
          color: "fg.subtle",
        },
        color: "fg.emphasized",
        borderBottom: "1px solid",
        height: "8",
        "padding-inline-start": "6",
        "padding-inline-end": "6",
        borderRadius: "0px",
        borderColor: "neutral.emphasized",
        _focus: {
          borderColor: "highlight.solid",
          "& + div": {
            fill: "fg.emphasized",
          },
        },
        _hover: {
          borderColor: "highlight.solid",
          "& + div": {
            fill: "fg.emphasized",
          },
        },
        _disabled: {
          borderColor: "border.emphasized",
          color: "fg.muted",
          "::placeholder": {
            color: "fg.muted",
          },
          "& + div": {
            fill: "fg.muted",
          },
        },
      },
    },
    //variant for Search component
    ghost: {
      element: {
        width: "4",
        fill: "fg.emphasized",
      },
      field: {
        _placeholder: {
          color: "fg.emphasized",
        },
        border: "none",
        color: "fg.emphasized",
        "padding-inline-start": "6",
        "padding-inline-end": "6",
        _hover: {
          "& + div": {
            //to control color of search icon
            fill: "fg.emphasized",
          },
        },
        _focus: {
          "& + div": {
            //to control color of search icon
            fill: "fg.emphasized",
          },
        },
        _disabled: {
          color: "fg.muted",
          "::placeholder": {
            color: "fg.muted",
          },
          "& + div": {
            fill: "fg.muted",
          },
        },
      },
    },
  },
});
